import { useState, useEffect, useRef, useCallback, lazy, Suspense } from "react";
import { faHome, faEllipsisVertical } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { NavItem } from "./NavItem";
import type { NavItemType } from "./NavItem";
import { ViewAsBanner } from "./ViewAsBanner";
import { canViewAsClient } from "@/utils/auth-utils";
import { User } from "@/tracker-types";
import { useDebounceCallback } from "@/hooks/useDebounceCallback";
import { NotificationBell } from "@/components/ui/NotificationBell/NotificationBell";

const ClientSelect = lazy(() => import("@/components/controls/Selects/ClientSelect"));

interface DesktopNavProps {
  user: { role?: string } | null;
  clientUser?: User | null;
  viewAsId: number | null;
  allNavItems: NavItemType[];
  handleViewAsChange: (clientId: number | null) => void;
  handleLogoutClick: () => void;
}

export function DesktopNav({
  user,
  clientUser,
  viewAsId,
  allNavItems,
  handleViewAsChange,
  handleLogoutClick,
}: DesktopNavProps) {
  const [showOverflowMenu, setShowOverflowMenu] = useState<boolean>(false);
  const [visibleCount, setVisibleCount] = useState<number | null>(null);
  const navContainerRef = useRef<HTMLDivElement>(null);
  const navItemsContainerRef = useRef<HTMLDivElement>(null);
  const overflowRef = useRef<HTMLDivElement>(null);

  const calculateVisibleItems = useCallback(() => {
    if (!navContainerRef.current || !navItemsContainerRef.current) return;

    const containerWidth = navContainerRef.current.offsetWidth;
    const hasClientSelect = canViewAsClient(user?.role) && !viewAsId;
    
    // Width Constants
    const CLIENT_SELECT_WIDTH = hasClientSelect ? 200 : 0;
    const MORE_BUTTON_WIDTH = 70;
    const HOME_NAV_WIDTH = 100;
    const CONTAINER_PADDING = 32;
    const ITEM_MARGIN = 16;

    const availableWidth = containerWidth - HOME_NAV_WIDTH - CLIENT_SELECT_WIDTH - CONTAINER_PADDING - MORE_BUTTON_WIDTH;

    const itemElements = navItemsContainerRef.current.querySelectorAll('.nav-item-measure');
    let totalWidth = 0;
    let count = 0;

    for (let i = 0; i < itemElements.length; i++) {
      const itemWidth = itemElements[i].getBoundingClientRect().width + ITEM_MARGIN;
      if (totalWidth + itemWidth <= availableWidth) {
        totalWidth += itemWidth;
        count++;
      } else {
        break;
      }
    }

    setVisibleCount(count);
  }, [user?.role, viewAsId]);

  const debouncedCalculateVisibleItems = useDebounceCallback(calculateVisibleItems, 100);

  useEffect(() => {
    calculateVisibleItems();

    const resizeObserver = new ResizeObserver(() => {
      debouncedCalculateVisibleItems();
    });

    if (navContainerRef.current) {
      resizeObserver.observe(navContainerRef.current);
    }

    return () => resizeObserver.disconnect();
  }, [calculateVisibleItems, debouncedCalculateVisibleItems]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Element;
      if (showOverflowMenu && overflowRef.current && !overflowRef.current.contains(target)) {
        setShowOverflowMenu(false);
      }
    };

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape" && showOverflowMenu) {
        setShowOverflowMenu(false);
      }
    };

    document.addEventListener("click", handleClickOutside);
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("click", handleClickOutside);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [showOverflowMenu]);

  const handleNavClick = () => {
    setShowOverflowMenu(false);
  };

  const visibleItems = visibleCount !== null ? allNavItems.slice(0, visibleCount) : allNavItems;
  const overflowItems = visibleCount !== null ? allNavItems.slice(visibleCount) : [];
  const showMoreButton = overflowItems.length > 1;
  const finalVisibleItems = showMoreButton ? visibleItems : [...visibleItems, ...overflowItems];
  const finalOverflowItems = showMoreButton ? overflowItems : [];

  const renderNavItem = (item: NavItemType, onClick?: () => void, inOverflow = false) => {
    if (item.path === "logout") {
      return (
        <button
          key="logout"
          onClick={handleLogoutClick}
          aria-label="Logout"
          title="Logout"
          className={`flex items-center px-4 py-2 rounded-lg transition-all duration-200 text-light/80 dark:text-dark/80 hover:bg-light-muted/80 dark:hover:bg-dark-surface/60 w-full${inOverflow ? " space-x-2" : ""}`}
        >
          <FontAwesomeIcon icon={item.icon} className="w-4 h-4" aria-hidden="true" />
          {inOverflow && <span className="font-medium">{item.label}</span>}
        </button>
      );
    }
    return (
      <NavItem
        key={item.path}
        to={item.path}
        icon={item.icon}
        label={item.label}
        variant="desktop"
        onClick={onClick}
        hideLabel={!inOverflow && item.level === 2}
      />
    );
  };

  // Find index of first level-2 item among visible items to insert divider
  const firstLevel2Index = finalVisibleItems.findIndex((item) => item.level === 2 || item.path === "logout");

  return (
    <nav ref={navContainerRef} className="sticky top-0 z-40 hidden w-full mx-auto border-b border-light-border xl:w-4/5 md:flex md:flex-col bg-light-inputbg dark:bg-dark-inputbg backdrop-blur-md dark:border-dark-border">
      {viewAsId && (
        <ViewAsBanner 
          clientUser={clientUser} 
          handleExitView={() => handleViewAsChange(null)} 
        />
      )}
      <div className="flex items-center justify-center w-full px-4 py-3 mx-auto">
        <div className="flex items-center justify-center space-x-4">
          <NavItem to="/" icon={faHome} label="Home" variant="desktop" hideLabel />

          {/* Hidden container for measuring item widths */}
          <div ref={navItemsContainerRef} className="flex items-center space-x-4 absolute invisible">
            {allNavItems.map((item) => (
              <div key={item.path} className="nav-item-measure">
                <NavItem
                  to={item.path}
                  icon={item.icon}
                  label={item.label}
                  variant="desktop"
                />
              </div>
            ))}
          </div>

          {/* Visible nav items with divider before first level-2 item */}
          {finalVisibleItems.map((item, index) => (
            <div key={item.path} className="flex items-center">
              {index === firstLevel2Index && firstLevel2Index > 0 && (
                <span className="mr-4 h-5 w-px bg-light-border dark:bg-dark-border" aria-hidden="true" />
              )}
              {renderNavItem(item)}
            </div>
          ))}

          {/* Overflow More button */}
          {finalOverflowItems.length > 0 && (
            <div ref={overflowRef} className="relative">
              <button
                onClick={() => setShowOverflowMenu(!showOverflowMenu)}
                aria-haspopup="menu"
                aria-expanded={showOverflowMenu}
                aria-label="More navigation options"
                title="More navigation options"
                className="flex items-center px-4 py-2 rounded-lg transition-all duration-200 text-light/80 dark:text-dark/80 hover:bg-light-muted/80 dark:hover:bg-dark-surface/60"
              >
                <FontAwesomeIcon icon={faEllipsisVertical} className="w-4 h-4" aria-hidden="true" />
              </button>

              {showOverflowMenu && (
                <div className="absolute right-0 top-full mt-2 w-48 rounded-lg shadow-lg border border-light-border dark:border-dark-border bg-light-background dark:bg-dark-background overflow-hidden z-50">
                  {finalOverflowItems.map((item) => renderNavItem(item, handleNavClick, true))}
                </div>
              )}
            </div>
          )}

          {canViewAsClient(user?.role) && !viewAsId && (
            <div className="w-48 ml-4 shrink-0">
              <Suspense fallback={<div className="h-9 w-full bg-light-border/20 rounded animate-pulse" />}>
                <ClientSelect
                  labelName=""
                  value={viewAsId}
                  onChange={handleViewAsChange}
                  className="h-9 text-sm"
                />
              </Suspense>
            </div>
          )}

          {user?.role === "client" && (
            <div className="ml-2 shrink-0">
              <NotificationBell />
            </div>
          )}
        </div>
      </div>
    </nav>
  );
}
